import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { useSnackbar } from "notistack";

import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import UpIcon from '@mui/icons-material/KeyboardArrowUp';
import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import {
    Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Fab, FormControl, Grid,
    IconButton, InputLabel,
    ListItem,
    ListItemText, NativeSelect, TextField, Typography, Select, Skeleton
} from "@mui/material";
import { green } from '@mui/material/colors';
import { useContext, useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { FixedSizeList } from 'react-window';
import { useGetWinterImportPostalCodes, useGetWinterWorkProjectLines, useGetWinterWorkSubtypes, useMoveProjectLineToList, useCreateWinterProjects } from "../api";
import { UserDataContext } from "../app/App";
import Layout from '../components/Layout/Layout';
import { PRIVILEGES } from "../constants";
import { hasPrivilege } from "../utils/privilegeUtils";
import ErrorPage from "./ErrorPage";


const ProjectLineListItem = ({ index, style, data }) => {
    const { line, isAdmin, onNavigateToLine, onClickOpenMoveToListDialog } = data[index];

    const estimatedHours = line?.project?.estimatedHours ?? 0;
    const formatEstimatedHours = (hours) => (hours % 1 === 0 ? hours : hours.toFixed(1));

    return (
        <ListItem
            sx={{ borderLeft: line?.checkIn ? `6px solid ${green[400]}` : '6px solid #b0b0b0' }}
            dense
            component="div"
            style={style}
            divider
            secondaryAction={
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography >
                        {`${estimatedHours > 0 ? `${formatEstimatedHours(estimatedHours)}` : ''}`}
                    </Typography>
                    <IconButton edge="end" aria-label="workline-details" onClick={onNavigateToLine}>
                        <ArrowForwardIosIcon />
                    </IconButton>
                </Box>
            }
        >
            {isAdmin && (
                <IconButton onClick={() => onClickOpenMoveToListDialog(line)} color="primary" sx={{ pl: 0, py: 0, pr: 2 }}>
                    <MenuOpenIcon />
                </IconButton>
            )}

            <ListItemText
                primary={line?.project?.address?.name ?? "Óskráð"}
                secondary={`${line?.product?.name ?? 'Óskráð'} - Serial: ${line?.serialNumber ?? 'Óskráð'}`}
                primaryTypographyProps={{
                    fontWeight: 'medium',
                    fontSize: "14px"
                }}
                secondaryTypographyProps={{
                    fontWeight: 'medium',
                    fontSize: "10px",

                }}
            />
        </ListItem>
    )
};

export default function WinterWork() {
    // Hooks
    const { t } = useTranslation();
    const history = useHistory(); // Navigate to other urls in the app
    const { user } = useContext(UserDataContext); // Get logged in user
    const { enqueueSnackbar } = useSnackbar(); // Display success/error popups

    const isAdmin = hasPrivilege(user.crmPrivileges, PRIVILEGES.WINTER_WORK_SUPER); // Check if user is admin

    // Local state
    const [projectLines, setProjectLines] = useState([]);
    const [filteredProjectLines, setFilteredProjectLines] = useState([]);
    const [isAdminView, setIsAdminView] = useState(false); // Is the user a admin?
    const [orderByAscending, setOrderByAscending] = useState(true); // Order by serial number
    const [showScrollToTop, setShowScrollToTop] = useState(null);  // Display scroll to top icon in list?
    const [selectedList, setSelectedList] = useState(() => Number(localStorage.getItem('selectedList') || 0)); // List number to filter lines 
    const [selectedPostalCode, setSelectedPostalCode] = useState(() => localStorage.getItem('selectedPostalCode') || "ShowAll"); // Postal code to filter lines

    // Admin input state
    const [firstListNumber, setFirstListNumber] = useState(""); // First list number to import in 
    const [numListsToCreate, setNumListsToCreate] = useState("");
    const [selectedProduct, setSelectedProduct] = useState([]); // Service subtypes selection
    const [selectedImportPostalCode, setSelectedImportPostalCode] = useState([]); // Import postal code selection
    const [selectedProjectLine, setSelectedProjectLine] = useState(null); // Stores selected project line
    const [moveToListDialogOpen, setMoveToListDialogOpen] = useState(false); // Controls dialog visibility
    const [selectedMoveToList, setSelectedMoveToList] = useState(0); // List number to filter lines

    // DOM refs
    const listRef = useRef(null); // Anchor for list container

    // API Queries
    const {
        data: projectLineData,
        isLoading: isLoadingProjectLines,
        isError: isErrorProjectLines,
        isSuccess: isSuccessProjectLines
    } = useGetWinterWorkProjectLines({
        // refetchOnWindowFocus: false,
        onSuccess: (data) => {
            setProjectLines(data.projectLines || []);
        },
    });
    const {
        data: subTypeData,
        isLoading: isLoadingSubTypes,
        isError: isErrorSubTypes
    } = useGetWinterWorkSubtypes({ refetchOnWindowFocus: false }); // Get winterwork service subtypes
    const {
        data: postalCodeData,
        isLoading: isLoadingImportPostalCodes,
        isError: isErrorImportPostalCodes
    } = useGetWinterImportPostalCodes({ refetchOnWindowFocus: false }); // Get post codes for importing lists

    // API Mutations
    const { mutate: moveProjectLineToList, isLoading: isLoadingMoveProjectLineToList } = useMoveProjectLineToList();
    const { mutate: createWinterProjects, isLoading: isLoadingCreateWinterProjects } = useCreateWinterProjects();

    // API Loading and error states
    const isLoading = isLoadingProjectLines || isLoadingSubTypes || isLoadingImportPostalCodes;
    const isError = isErrorProjectLines || isErrorSubTypes || isErrorImportPostalCodes;

    // API Data
    const availableListsNumbers = projectLineData?.availableListsNumbers || [];
    const serviceSubTypes = subTypeData?.serviceSubTypes || [];
    const importPostalCodes = postalCodeData?.importPostalCodes || [];

    // Handlers
    const handleOnAdminToggle = isAdmin ? () => setIsAdminView(prev => !prev) : null;
    const handleOnChangeSelectedList = (e) => {
        const newList = Number(e.target.value);
        setSelectedList(newList);
        localStorage.setItem('selectedList', newList.toString());
    };
    const handleOnChangeSelectedPostalCode = (e) => {
        const newPostalCode = e.target.value;
        setSelectedPostalCode(newPostalCode);
        localStorage.setItem('selectedPostalCode', newPostalCode.toString());
    };
    const handleOnClickOrderByAscending = () => setOrderByAscending(prev => !prev);
    const handleOnNavigateToLine = (lineId) => history.push(`/winterwork/${lineId}`, { selectedList, selectedPostalCode });

    // Create winterwork handlers
    const handleOnChangeFirstListNumber = (e) => setFirstListNumber(Number(e.target.value));
    const handleOnChangeNumListsToCreate = (e) => setNumListsToCreate(Number(e.target.value))
    const handleOnChangeSelectedProduct = (e) => {
        const { options } = e.target;
        const value = [];
        for (let i = 0, l = options.length; i < l; i += 1) {
            if (options[i].selected) {
                value.push(options[i].value);
            }
        }
        setSelectedProduct(value);
    };
    const handleOnChangeSelectedImportPostalCode = (e) => {
        const { options } = e.target;
        const value = [];
        for (let i = 0, l = options.length; i < l; i += 1) {
            if (options[i].selected) {
                value.push(options[i].value);
            }
        }
        setSelectedImportPostalCode(value);
    };

    // Move to list handlers
    const handleChangeSelectedMoveToList = (e) => setSelectedMoveToList(Number(e.target.value));
    const handleOnClickOpenMoveToListDialog = useCallback((projectLine) => {
        setSelectedProjectLine(projectLine);

        const firstMoveToList = availableListsNumbers.find(
            (listNumber) => listNumber !== projectLine.listId
        );

        setSelectedMoveToList(firstMoveToList || 0);
        setMoveToListDialogOpen(true);
    }, [selectedList]);
    const handleOnCloseMoveToListDialog = useCallback(() => {
        if (!isLoadingMoveProjectLineToList) {
            setSelectedProjectLine(null);
            setSelectedMoveToList(0);
        }
        setMoveToListDialogOpen(false);
    }, [isLoadingMoveProjectLineToList]);

    // API handlers
    const handleOnClickMoveProjectLineToList = useCallback(async () => {
        if (!selectedProjectLine) return;
        if (selectedProjectLine.listId === selectedMoveToList) return; // Don't move line to same list

        // Optimistic update: remove project line from the `projectLines` list directly
        const previousProjectLines = [...projectLines];
        const updatedProjectLines = projectLines.map(line => {
            if (line.id === selectedProjectLine.id) {
                return { ...line, listId: selectedMoveToList }; // Update listId
            }
            return line;
        });
        setProjectLines(updatedProjectLines);

        moveProjectLineToList(
            {
                projectLineId: selectedProjectLine.id,
                selectedList: selectedMoveToList,
            },
            {
                onSuccess: () => {
                    enqueueSnackbar(`Moved ${selectedProjectLine?.name} to list #${selectedMoveToList}`, {
                        variant: "success",
                        anchorOrigin: { vertical: "bottom", horizontal: "center" },
                        autoHideDuration: 3500,
                    });
                    handleOnCloseMoveToListDialog(); // Close dialog on success
                },
                onError: () => {
                    enqueueSnackbar(`Error moving ${selectedProjectLine?.name || ""} to list #${selectedMoveToList}`, {
                        variant: "error",
                        anchorOrigin: { vertical: "bottom", horizontal: "center" },
                        autoHideDuration: 3500,
                    });
                    setProjectLines(previousProjectLines);
                },
            }
        );
    }, [selectedProjectLine, selectedMoveToList, projectLines, moveProjectLineToList, enqueueSnackbar]);

    const handleOnClickCreateWinterProjects = useCallback(async () => {
        const productsList = selectedProduct.map(v => ({ ProductName: v }))
        const selectedImportPostalCodes = selectedImportPostalCode.toString();

        createWinterProjects(
            {
                serviceType: "Vetrarverk",
                selectedProduct: productsList,
                firstListNumber,
                numListsToCreate,
                selectedImportPostalCode: selectedImportPostalCodes
            },
            {
                onSuccess: () => {
                    enqueueSnackbar("List imported", {
                        variant: "success",
                        anchorOrigin: { vertical: "bottom", horizontal: "center" },
                        autoHideDuration: 3500,
                    });
                },
                onError: () => {
                    enqueueSnackbar("Error occurred when importing list", {
                        variant: "error",
                        anchorOrigin: { vertical: "bottom", horizontal: "center" },
                        autoHideDuration: 3500,
                    });
                },
            }
        );
    }, [selectedProduct, firstListNumber, numListsToCreate, selectedImportPostalCode, enqueueSnackbar]);

    // Scroll handlers for line list
    const handleScroll = ({ scrollOffset }) => {
        setShowScrollToTop(scrollOffset > 100);
    };
    const scrollToTop = () => {
        if (listRef.current) {
            listRef.current.scrollTo({ top: 0, behavior: 'smooth' });
        }
    }

    // Get postal codes for lines in selected list
    const availablePostalCodes = useMemo(() => {
        const postalCodes = [
            "ShowAll",
            ...new Set(projectLines
                .filter(line => line.listId === selectedList)
                .map(line => line.project?.address?.postalCode)
                .filter(Boolean)
                .sort((a, b) => a - b)
            )
        ];
        return postalCodes;
    }, [projectLines, selectedList]);

    // Filter project lines by selected list and postal code
    useEffect(() => {
        let filtered = projectLines.filter(line => line.listId === selectedList);

        if (selectedPostalCode !== "ShowAll") {
            filtered = filtered.filter(line => line.project?.address?.postalCode === selectedPostalCode);
        }

        filtered.sort((a, b) => {
            if (orderByAscending) {
                return a.serialNumber > b.serialNumber ? 1 : -1;
            }
            return a.serialNumber < b.serialNumber ? 1 : -1;
        });

        setFilteredProjectLines(filtered);
    }, [projectLines, selectedList, selectedPostalCode, orderByAscending]);

    // Set selected list as first available list
    useEffect(() => {
        if (availableListsNumbers && availableListsNumbers.length > 0 && !availableListsNumbers.includes(selectedList)) {
            const newList = availableListsNumbers[0];
            setSelectedList(newList);
            localStorage.setItem('selectedList', newList.toString());
        }
    }, [availableListsNumbers, selectedList]);

    // Set the selected postal code as the first available postal code when project lines change
    useEffect(() => {
        if (availablePostalCodes && availablePostalCodes.length > 0) {
            const storedPostalCode = localStorage.getItem('selectedPostalCode');

            if (storedPostalCode && availablePostalCodes.includes(storedPostalCode)) {
                setSelectedPostalCode(storedPostalCode);
            } else if (!availablePostalCodes.includes(selectedPostalCode)) {
                setSelectedPostalCode('ShowAll');
            }
        }
    }, [availablePostalCodes]);

    useEffect(() => {
        if (isAdminView && importPostalCodes.length > 0 && availablePostalCodes.length > 0) {
            const matchingPostalCodes = importPostalCodes;
            // const matchingPostalCodes = importPostalCodes.filter(code => availablePostalCodes.includes(code));
            setSelectedImportPostalCode(matchingPostalCodes);
        }
    }, [isAdminView, importPostalCodes, availablePostalCodes]);


    // Render error and loading pages
    if (isError) {
        return <ErrorPage />;
    }
    if (isLoading) {
        return (
            <Layout color="rgb(77 106 201)" title={t("Winterwork")} subtitle={user.name} backBtnNavigateHome={true}>
                <Box display="flex" justifyContent="center">
                    <CircularProgress />
                </Box>
            </Layout>
        );
    }
    if (isLoadingMoveProjectLineToList) {
        enqueueSnackbar(`Moving ${selectedProjectLine?.name || ""} to list #${selectedMoveToList} ...`, {
            variant: "info",
            anchorOrigin: { vertical: "bottom", horizontal: "center" },
            preventDuplicate: true
        });
    }
    if (isLoadingCreateWinterProjects) {
        enqueueSnackbar("Importing lists ...", {
            variant: "info",
            anchorOrigin: { vertical: "bottom", horizontal: "center" },
            preventDuplicate: true
        });
    }
    return (
        <Layout
            color="rgb(77 106 201)"
            title={t("Winterwork")}
            subtitle={user.name}
            onAdminToggle={handleOnAdminToggle}
            isAdminView={isAdminView}
            backBtnNavigateHome={true}
        >
            <Grid container direction="column" spacing={2}>
                {/* List & postal code selection */}
                <Grid item>
                    {/* List selection */}
                    <FormControl fullWidth margin="dense">
                        <InputLabel variant="standard" htmlFor="select-list-native">
                            Select List
                        </InputLabel>
                        <NativeSelect
                            variant="outlined"
                            value={selectedList}
                            onChange={handleOnChangeSelectedList}
                            inputProps={{ name: 'select-list', id: 'select-list-native' }}
                        >
                            {availableListsNumbers?.map((listNumber) => (
                                <option value={listNumber} key={listNumber}>{t('ListNumber')} {listNumber}</option>
                            ))}
                        </NativeSelect>
                    </FormControl>

                    {/* Postal code selection */}
                    <FormControl fullWidth margin="dense">
                        <InputLabel variant="standard" htmlFor="select-postalcode-native">
                            Postal Code
                        </InputLabel>
                        <NativeSelect
                            value={selectedPostalCode}
                            onChange={handleOnChangeSelectedPostalCode}
                            inputProps={{ name: 'select-postalcode', id: 'select-postalcode-native' }}
                        >
                            {availablePostalCodes?.map((postalCode) => (
                                <option value={postalCode} key={postalCode}>
                                    {postalCode === "ShowAll" ? t("ShowAll") : postalCode}
                                </option>
                            ))}
                        </NativeSelect>
                    </FormControl>
                </Grid>

                {/* Total number of lines & sort button */}
                <Grid item sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box sx={{ display: 'flex' }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', pr: 1 }}>
                            {filteredProjectLines.length}
                        </Typography>
                        <Typography variant="subtitle1">
                            {filteredProjectLines.length === 1 ? t("Workline") : t("Worklines")}
                        </Typography>
                    </Box>
                    <IconButton onClick={handleOnClickOrderByAscending}>
                        {orderByAscending ? <ArrowDownwardIcon /> : <ArrowUpwardIcon />}
                    </IconButton>
                </Grid>

                {/* Scrollable worklines */}
                <Grid item xs>
                    {filteredProjectLines.length === 0 && !isSuccessProjectLines ? (
                        <Skeleton variant="rectangular" width="100%" animation="wave" height={550} />
                    ) : (
                        <>
                            <FixedSizeList
                                height={550}
                                width="100%"
                                itemSize={50}
                                itemCount={filteredProjectLines.length}
                                itemData={filteredProjectLines.map((line) => ({
                                    line,
                                    isAdmin,
                                    onNavigateToLine: () => handleOnNavigateToLine(line.id),
                                    onClickOpenMoveToListDialog: handleOnClickOpenMoveToListDialog
                                }))}
                                ref={listRef}
                                onScroll={handleScroll}
                            >
                                {ProjectLineListItem}
                            </FixedSizeList>
                            {showScrollToTop && (
                                <Fab
                                    onClick={scrollToTop}
                                    size="small"
                                    color="primary"
                                    sx={{
                                        zIndex: 1000,
                                        position: 'absolute',
                                        bottom: 16,
                                        right: 16,
                                    }}
                                >
                                    <UpIcon />
                                </Fab >
                            )}
                        </>
                    )
                }
                </Grid>
            </Grid>

            <Dialog open={moveToListDialogOpen} fullWidth onClose={handleOnCloseMoveToListDialog}>
                <DialogTitle>
                    {selectedProjectLine?.project?.address?.name ?? ""}
                </DialogTitle>
                <DialogContent>
                    <FormControl fullWidth margin="dense">
                        <InputLabel variant="standard" htmlFor="select-move-to-list-native">
                            Select a list to move to
                        </InputLabel>
                        <NativeSelect
                            variant="outlined"
                            value={selectedMoveToList}
                            onChange={handleChangeSelectedMoveToList}
                            inputProps={{ name: 'select-move-to=list', id: 'select-move-to-list-native' }}
                        >
                            {availableListsNumbers?.map((listNumber) => {
                                if (listNumber === selectedList) return null;
                                return (
                                    <option value={listNumber} key={listNumber}>
                                        {t('ListNumber')} {listNumber}
                                    </option>
                                );
                            })}
                        </NativeSelect>
                    </FormControl>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleOnCloseMoveToListDialog}>
                        Cancel
                    </Button>
                    <Button onClick={handleOnClickMoveProjectLineToList} disabled={isLoadingMoveProjectLineToList}>
                        Move to List
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={isAdminView} onClose={handleOnAdminToggle}>
                <DialogTitle>{t("CreateWinterProjects")}</DialogTitle>
                <DialogContent>

                    {/* Postal codes */}
                    <FormControl margin="dense" fullWidth>
                        <InputLabel shrink htmlFor="select-import-postal-codes-native">
                            {t("PostalCodes")}
                        </InputLabel>
                        <Select
                            multiple
                            native
                            value={selectedImportPostalCode}
                            onChange={handleOnChangeSelectedImportPostalCode}
                            renderValue={(selected) => selected.join(', ')}
                            inputProps={{
                                name: 'select-import-postal-codes',
                                id: 'select-import-postal-codes-native'
                            }}
                        >
                            {importPostalCodes?.map((postalCode) => (
                                <option value={postalCode} key={postalCode}>{postalCode}</option>
                            ))}
                        </Select>
                    </FormControl>

                    {/* Service / product multi select */}
                    <FormControl margin="dense" fullWidth>
                        <InputLabel shrink htmlFor="select-product-native">
                            {t("Products")}
                        </InputLabel>
                        <Select
                            multiple
                            native
                            value={selectedProduct}
                            renderValue={(selected) => selected.join(', ')}
                            onChange={handleOnChangeSelectedProduct}
                            inputProps={{
                                name: 'select-product',
                                id: 'select-product-native'
                            }}
                        >
                            {serviceSubTypes?.map((subType) => (
                                <option value={subType.name} key={subType.id}>{subType.name}</option>
                            ))}
                        </Select>
                    </FormControl>

                    {/* Number of lists to create*/}
                    <TextField
                        margin="dense"
                        type="number"
                        id="num-list-to-create"
                        label="Number of lists to create"
                        fullWidth
                        value={numListsToCreate}
                        onChange={handleOnChangeNumListsToCreate}
                        inputProps={{ min: 1 }}
                    />

                    {/* First list number */}
                    <TextField
                        margin="dense"
                        type="number"
                        id="first-list-number"
                        label="First list number"
                        fullWidth
                        value={firstListNumber}
                        onChange={handleOnChangeFirstListNumber}
                        inputProps={{ min: 1 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleOnAdminToggle}>Cancel</Button>
                    <Button
                        onClick={handleOnClickCreateWinterProjects}
                        disabled={isLoadingCreateWinterProjects || selectedProduct.length === 0 || selectedImportPostalCode.length === 0}
                    >
                        Import
                    </Button>
                </DialogActions>
            </Dialog>
        </Layout>
    );
}
